import type { ReactNode } from "react";

import { Badge } from "@/components/ui/badge";

// Central status → Badge-variant map (DESIGN.md › Components › Badges): status
// labels are gray (`secondary`) by default; `success` only for genuinely positive
// terminal states; `destructive` only for hard errors. Add new statuses here —
// never pick a variant (or hand-roll a color) at the call site.
const SUCCESS_STATUSES = new Set([
  "completed",
  "signed",
  "executed",
  "active",
  "delivered",
  "suitable",
  "success",
  "succeeded",
  "verified",
  "approved",
  "accepted",
  "GREEN",
  // QL-21 — compliance screening: no PEP or sanction hit.
  "clear",
]);

// QL-21 — `sanction` is a compliance screening hit that must stop the advice.
const DESTRUCTIVE_STATUSES = new Set([
  "failed",
  "error",
  "rejected",
  "cancelled",
  "RED",
  "sanction",
]);

export function statusBadgeVariant(status: string): "secondary" | "success" | "destructive" {
  if (SUCCESS_STATUSES.has(status)) return "success";
  if (DESTRUCTIVE_STATUSES.has(status)) return "destructive";
  return "secondary";
}

interface StatusBadgeProps {
  /** Raw status value (e.g. "completed", "draft") — picks the variant. */
  status: string;
  /** Translated label; falls back to the raw status value. */
  children?: ReactNode;
  className?: string;
}

export function StatusBadge({ status, children, className }: StatusBadgeProps) {
  return (
    <Badge variant={statusBadgeVariant(status)} className={className}>
      {children ?? status}
    </Badge>
  );
}
